import type { IncidentReport } from '@lmc/ts-common'
import { IncidentCaseVersionEnum } from '@lmc/validations'

/**
 * Whether an incident report's case belongs to the v1 form.
 *
 * Only a case the v1 form opened does. No case at all, or the mobile
 * questionnaire's pre-populated `isDraft: true` case, has not been opened by
 * either form, so it goes to v2 — and a case v2 opened reopens in v2, so a user
 * can carry on where they left off.
 *
 * `version` is absent on every case the v1 form has ever opened, which is why
 * the test is "not v2" rather than "is v1".
 */
export const usesV1Form = (
  incidentReport: Pick<IncidentReport, 'case'>
): boolean => {
  const incidentCase = openedCase(incidentReport)

  return Boolean(
    incidentCase && incidentCase.version !== IncidentCaseVersionEnum.v2
  )
}

/** Whether the v2 form opened the case; it then opens in v2, whatever the flag. */
export const isV2Case = (incidentReport: Pick<IncidentReport, 'case'>) =>
  openedCase(incidentReport)?.version === IncidentCaseVersionEnum.v2

/** The case, unless there is none or it is only the mobile draft. */
const openedCase = (incidentReport: Pick<IncidentReport, 'case'>) => {
  // `version` is not on the ts-common case type yet; narrowed by name, not `any`.
  const incidentCase = incidentReport.case as
    | (IncidentReport['case'] & { version?: IncidentCaseVersionEnum })
    | null
    | undefined

  return incidentCase && !incidentCase.isDraft ? incidentCase : null
}
